// src/components/ToolbarOverflowMenu.jsx
//
// The "..." menu of the traffic toolbar: rarely used actions (New session, Clear,
// Import, Export, Sign-off) moved out of the main row. Every former action stays
// reachable in two clicks (open the menu, pick the item); no handler was removed.
// Slot Story step 4, AC 3.10-3.11.
import React, { useEffect, useRef, useState } from 'react';

/**
 * items: [{ key, label, onClick, title?, danger?, disabled?, hidden? }]
 * Hidden items are not rendered; the menu itself renders nothing when empty.
 */
export default function ToolbarOverflowMenu({ items = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const shown = items.filter((i) => i && !i.hidden);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!shown.length) return null;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        title="More actions: New session, Clear, Import, Export, Sign-off"
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: '4px 10px', fontSize: 14, lineHeight: 1, borderRadius: 6, cursor: 'pointer',
          border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)',
        }}
      >
        {'⋯'}
      </button>
      {open && (
        <div
          role="menu"
          data-testid="overflow-menu"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 60, minWidth: 190,
            background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)', padding: 4, display: 'flex', flexDirection: 'column',
          }}
        >
          {shown.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              disabled={!!it.disabled}
              title={it.title}
              onClick={() => { setOpen(false); if (it.onClick) it.onClick(); }}
              style={{
                textAlign: 'left', padding: '6px 10px', fontSize: 12, border: 'none', borderRadius: 6,
                background: 'transparent', cursor: it.disabled ? 'not-allowed' : 'pointer',
                color: it.danger ? 'var(--error-text)' : 'var(--ink)', opacity: it.disabled ? 0.45 : 1,
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
